import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Truck,
  RefreshCw,
  Award,
  MessageCircle,
} from 'lucide-react';
import API from '../utils/api';
import config from '../utils/config';
import ProductCard from '../components/ProductCard';

const FALLBACK = [
  { _id:'1', name:'Premium Stainless Steel Ring', slug:'premium-stainless-steel-ring', category:'rings', price:999, discountPrice:799, tags:['bestseller'], isFeatured:true, images:[{url:'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=400'}] },
  { _id:'2', name:'Elegant Drop Earrings', slug:'elegant-drop-earrings', category:'earrings', price:799, discountPrice:599, tags:['new'], isNewArrival:true, images:[{url:'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=400'}] },
  { _id:'3', name:'Women Stainless Steel Necklace', slug:'women-stainless-steel-necklace', category:'necklace', price:1499, discountPrice:1199, tags:['bestseller'], isFeatured:true, images:[{url:'https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=400'}] },
  { _id:'4', name:'Men Kada Bracelet', slug:'men-kada-bracelet', category:'kada-bracelet', price:1199, discountPrice:899, tags:['new'], isNewArrival:true, images:[{url:'https://images.unsplash.com/photo-1573408301185-9519f94816b5?w=400'}] },
];

const categories = [
  { slug:'rings', label:'Rings', image:'/images/category-rings.jpg', section:'women' },
  { slug:'earrings', label:'Earrings', image:'/images/category-earrings.jpg', section:'women' },
  { slug:'necklace', label:'Necklace', image:'/images/category-necklace.jpg', section:'women' },
  { slug:'kada-bracelet', label:'Kada Bracelet', image:'/images/category-kada-bracelet.jpg', section:'women' },
  { slug:'chain-bracelet', label:'Chain Bracelet', image:'/images/category-chain-bracelet.jpg', section:'women' },
  { slug:'bangles', label:'Bangles', image:'/images/category-bangles.jpg', section:'women' },
  { slug:'watches', label:'Watches', image:'/images/category-watches.jpg', section:'women' },
  { slug:'chains', label:'Men Chains', image:'/images/category-chains.jpg', section:'men' },
];

const promises = [
  { icon: Award, title: 'Premium Stainless Steel', desc: 'Durable, stylish and crafted for everyday elegance' },
  { icon: Shield, title: 'Tarnish Resistant', desc: 'Made to keep its premium finish with proper care' },
  { icon: Truck, title: 'Fast Delivery', desc: 'Safe packaging and reliable delivery support' },
  { icon: RefreshCw, title: 'Replacement Support', desc: 'Replacement support for damaged products with unboxing video' },
];

export default function Home() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [instagramImages, setInstagramImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [f, n] = await Promise.all([
          API.get('/products?tag=featured&limit=6'),
          API.get('/products?tag=new&limit=6'),
        ]);

        const featuredProducts = f.data.products?.length
          ? f.data.products
          : FALLBACK.filter((p) => p.isFeatured);

        const newProducts = n.data.products?.length
          ? n.data.products
          : FALLBACK.filter((p) => p.isNewArrival);

        setFeatured(featuredProducts.slice(0, 4));
        setNewArrivals(newProducts.slice(0, 4));

        const galleryImages = [...featuredProducts, ...newProducts]
          .flatMap((p) => p.images || [])
          .map((img) => img.url)
          .filter(Boolean)
          .slice(0, 6);

        setInstagramImages(galleryImages);
      } catch {
        const featuredProducts = FALLBACK.filter((p) => p.isFeatured);
        const newProducts = FALLBACK.filter((p) => p.isNewArrival);

        setFeatured(featuredProducts.slice(0, 4));
        setNewArrivals(newProducts.slice(0, 4));

        const galleryImages = FALLBACK
          .flatMap((p) => p.images || [])
          .map((img) => img.url)
          .filter(Boolean)
          .slice(0, 6);

        setInstagramImages(galleryImages);
      }

      setLoading(false);
    }

    load();
  }, []);

  return (
    <div className="page-enter">
      <section className="premium-hero">
        <div className="hero-overlay" />

        <div className="hero-content">
          <p className="hero-eyebrow"></p>

          <h1 className="hero-title">
            Elevate Your
            <br />
            <em>Everyday Style</em>
          </h1>

          <p className="hero-desc">
            Discover premium stainless steel rings, chains, bracelets, watches
            and fashion accessories crafted for modern elegance.
          </p>

          <div className="hero-actions">
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/collections')}>
              Explore Collections
            </button>
          </div>
        </div>

        
        
        
        <style>{`

        .page-enter {
  width: 100%;
  overflow-x: hidden;
}

.section {
  width: 100%;
  overflow-x: hidden;
}

body,
html,
#root {
  overflow-x: hidden;
}




  .premium-hero {
    position: relative;
    min-height: 86vh;
    display: flex;
    align-items: center;
    background:
      linear-gradient(90deg, rgba(40,10,18,0.88), rgba(40,10,18,0.45)),
      url("/images/hero-jewellery.jpg") center/cover no-repeat;
    overflow: hidden;
  }

 .hero-content {
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 90px 24px;
  width: 100%;
  box-sizing: border-box;
}

  .hero-eyebrow {
    font-size: 11px;
    letter-spacing: 4px;
    text-transform: uppercase;
    color: #C9A96E;
    margin-bottom: 18px;
  }

  .hero-title {
    font-family: "Cormorant Garamond", serif;
    font-size: clamp(42px, 7vw, 86px);
    font-weight: 300;
    line-height: 1.05;
    color: #fff;
    max-width: 760px;
    margin-bottom: 22px;
  }

  .hero-title em {
    color: #E5C27A;
    font-style: italic;
  }

  .hero-desc {
    font-size: 16px;
    line-height: 1.8;
    color: rgba(255,255,255,0.78);
    max-width: 520px;
    margin-bottom: 30px;
  }

  .hero-actions {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
  }

  .category-card {
    position: relative;
    height: 320px;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    background: #ddd;
    box-shadow: 0 12px 28px rgba(107,27,42,0.08);
    transition: transform 0.25s ease;
  }

  .category-card img,
  .instagram-tile img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.55s ease;
  }

  .category-card::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 20%, rgba(40,10,18,0.78));
  }

  .category-name {
    position: absolute;
    z-index: 2;
    left: 18px;
    right: 18px;
    bottom: 18px;
    color: white;
    font-family: "Cormorant Garamond", serif;
    font-size: 24px;
    letter-spacing: 1px;
    line-height: 1.1;
  }

  .category-shop {
    position: absolute;
    z-index: 2;
    left: 18px;
    bottom: 52px;
    color: #E5C27A;
    font-size: 11px;
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  @media(max-width:768px) {
    .section {
    padding-left: 16px !important;
    padding-right: 16px !important;
    width: 100%;
    .product-card {
  min-height: 290px;
}

.prod-img-wrap {
  height: 160px;
}

.prod-name {
  font-size: 15px;
  min-height: 38px;
}

.prod-price {
  font-size: 14px;
}

.prod-info {
  padding: 10px;
}
  }

  .instagram-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }
    .premium-hero {
      min-height: 72vh;
      align-items: flex-end;
      background:
        linear-gradient(180deg, rgba(40,10,18,0.38), rgba(40,10,18,0.92)),
        url("/images/hero-jewellery.jpg") center top/cover no-repeat;
    }

    .hero-content {
      padding: 110px 18px 46px;
    }

    .hero-eyebrow {
      font-size: 10px;
      letter-spacing: 2.5px;
      margin-bottom: 12px;
    }

    .hero-title {
      font-size: 42px;
      line-height: 1.02;
      margin-bottom: 14px;
    }

    .hero-desc {
      font-size: 14px;
      line-height: 1.65;
      margin-bottom: 22px;
    }

    .hero-actions .btn {
      width: 100%;
      padding: 14px 18px;
    }

    .section {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }

    .category-card {
      height: 240px;
      border-radius: 10px;
    }

    .category-name {
      font-size: 22px;
      left: 16px;
      right: 16px;
      bottom: 18px;
    }

    .category-shop {
      left: 16px;
      bottom: 48px;
      font-size: 10px;
    }

    .instagram-grid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
      .category-card:active img {
  transform: scale(1.04);
}

.category-card:active {
  transform: scale(0.98);
}

.category-card {
  transition: transform 0.25s ease;
}

.category-shop {
  bottom: 48px;
  opacity: 1;
}
  }

  @media(max-width:480px) {
    .premium-hero {
      min-height: 68vh;
    }

    .hero-title {
      font-size: 38px;
    }

    .hero-desc {
      font-size: 13px;
    }

    .category-card {
      height: 220px;
    }
  }
    .category-card:hover img {
  transform: scale(1.08);
  .category-card:active {
  transform: scale(0.98);
}

.category-card:active img {
  transform: scale(1.04);
}
}

.category-card:hover .category-shop {
  bottom: 52px;
}
`}</style>
      </section>

      <section style={{ background: 'var(--cream)', padding: '70px 0' }}>
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <p className="section-eyebrow">Shop by Collection</p>
          <h2 className="section-title">Men & Women Collections</h2>
          <div className="divider"><div className="divider-line" /><span className="divider-gem">AD</span><div className="divider-line" /></div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 20 }}>
            <div className="category-card" onClick={() => navigate('/collections?section=women')}>
              <img src="/images/women-collection.jpg" alt="Women's Collection" onError={(e) => { e.currentTarget.src = '/images/jewelry-placeholder.jpg'; }} />
              <div className="category-shop">Shop Now</div>
              <div className="category-name">Women's Collection</div>
        
            </div>

            <div className="category-card" onClick={() => navigate('/collections?section=men')}>
              <img src="/images/men-collection.jpg" alt="Men's Collection" onError={(e) => { e.currentTarget.src = '/images/jewelry-placeholder.jpg'; }} />
              <div className="category-shop">Shop Now</div>
              <div className="category-name">Men's Collection</div>
               
            </div>
          </div>
        </div>
      </section>

      <section style={{ background:'white', padding:'80px 0' }}>
        <div className="section" style={{ paddingTop:0, paddingBottom:0 }}>
          <p className="section-eyebrow">Shop by Category</p>
          <h2 className="section-title">Our Collections</h2>
          <div className="divider"><div className="divider-line" /><span className="divider-gem">AD</span><div className="divider-line" /></div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:18 }}>
            {categories.map(({ slug, label, image, section }) => (
              <div
                className="category-card"
                key={`${section}-${slug}`}
                onClick={() => navigate(`/collections?section=${section}&category=${slug}`)}
              >
                <img src={image} alt={label} onError={(e) => { e.currentTarget.src = '/images/jewelry-placeholder.jpg'; }} />
                <div className="category-shop">Shop Now</div>
                <div className="category-name">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding:'80px 0' }}>
        <div className="section" style={{ paddingTop:0, paddingBottom:0 }}>
          <p className="section-eyebrow">Handpicked for You</p>
          <h2 className="section-title">Best Sellers</h2>
          <div className="divider"><div className="divider-line" /><span className="divider-gem">AD</span><div className="divider-line" /></div>

          {loading ? <div className="spinner" /> : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:24 }}>
              {featured.map((p) => <ProductCard key={p._id} product={p} />)}
            </div>
          )}

          <div style={{ textAlign:'center', marginTop:40 }}>
            <button className="btn btn-outline btn-lg" onClick={() => navigate('/collections')}>View All Products</button>
          </div>
        </div>
      </section>

      <section style={{ background:'var(--cream)', padding:'80px 0' }}>
        <div className="section" style={{ paddingTop:0, paddingBottom:0 }}>
          <p className="section-eyebrow">Why Anvith Dazzle</p>
          <h2 className="section-title">The Anvith Promise</h2>
          <div className="divider"><div className="divider-line" /><span className="divider-gem">AD</span><div className="divider-line" /></div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:32 }}>
            {promises.map(({ icon: Icon, title, desc }) => (
              <div key={title} style={{ textAlign:'center' }}>
                <div style={{ width:60, height:60, background:'white', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', border:'1px solid var(--border)' }}>
                  <Icon size={24} color="var(--maroon)" />
                </div>
                <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, marginBottom:8, color:'var(--text-dark)' }}>{title}</h3>
                <p style={{ fontSize:13, color:'var(--text-light)', lineHeight:1.7 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding:'80px 0' }}>
        <div className="section" style={{ paddingTop:0, paddingBottom:0 }}>
          <p className="section-eyebrow">Just In</p>
          <h2 className="section-title">New Arrivals</h2>
          <div className="divider"><div className="divider-line" /><span className="divider-gem">AD</span><div className="divider-line" /></div>

          {loading ? <div className="spinner" /> : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:24 }}>
              {newArrivals.map((p) => <ProductCard key={p._id} product={p} />)}
            </div>
          )}
        </div>
      </section>

      <section style={{ background:'var(--maroon)', padding:'64px 24px', textAlign:'center' }}>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(28px,4vw,40px)', color:'#C9A96E', fontWeight:300, marginBottom:12 }}>Need Help Choosing?</h2>
        <p style={{ color:'rgba(255,255,255,0.7)', fontSize:15, marginBottom:32 }}>Chat with our jewellery experts for personalised guidance</p>
        <a href={config.whatsappUrl()} target="_blank" rel="noreferrer" className="btn btn-whatsapp btn-lg" style={{ textDecoration:'none', display:'inline-flex', gap:8, alignItems:'center' }}>
          <MessageCircle size={18} />
          Chat on WhatsApp
        </a>
      </section>

      <section style={{ padding:'80px 0' }}>
        <div className="section" style={{ paddingTop:0, paddingBottom:0 }}>
          <p className="section-eyebrow">Follow @{config.instagramHandle}</p>
          <h2 className="section-title">Instagram Gallery</h2>
          <div className="divider"><div className="divider-line" /><span className="divider-gem">IG</span><div className="divider-line" /></div>

          <div
  className="instagram-grid"
  style={{
    display:'grid',
    gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',
    gap:8,
    width:'100%',
  }}
>
            {instagramImages.map((image, index) => (
              <a
                key={`${image}-${index}`}
                href={config.instagramUrl}
                target="_blank"
                rel="noreferrer"
                className="instagram-tile"
                style={{
                  aspectRatio:1,
                  borderRadius:4,
                  overflow:'hidden',
                  display:'block',
                  background:'var(--cream)',
                  border:'1px solid var(--border)',
                }}
              >
                <img src={image} alt="Anvith Dazzle jewellery" />
              </a>
            ))}
          </div>

          <div style={{ textAlign:'center', marginTop:24 }}>
            <a href={config.instagramUrl} target="_blank" rel="noreferrer" className="btn btn-outline">Follow on Instagram</a>
          </div>
        </div>
      </section>
    </div>
  );
}